import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";
import { usePortalAuth, usePortalRecords } from "../../application/usePortalAuth";
import {
  panelChoice,
  recordKind,
  recordLabel,
  recordSubtitle,
  recordSubtitleKind,
} from "../../domain/recordLabel";
import { AuthLayout } from "../../../auth";

/**
 * Which panel to open, asked once per tab when there is more than one.
 *
 * An account with a single record never reaches this screen: the guard settles
 * it before routing here, and the redirect below covers a direct visit.
 */
export default function PortalChooserPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isPatient } = usePortalAuth();
  const { records, loadingRecords, failedToLoad, switchTo } = usePortalRecords(isPatient);

  useEffect(() => {
    if (loadingRecords || failedToLoad) return;
    if (records.length === 1) {
      // Nothing to choose. Also covers an account that lost a record while
      // this tab was open.
      switchTo(records[0].patient_id);
      navigate("/portal", { replace: true });
    }
  }, [loadingRecords, failedToLoad, records, switchTo, navigate]);

  if (loadingRecords || records.length <= 1) return null;

  const choice = panelChoice(records);
  const title = {
    course: t("portal.chooser.chooseCourse", "Choose your course"),
    professional: t("portal.chooser.chooseProfessional", "Choose your therapist"),
    mentor: t("portal.chooser.chooseMentor", "Choose your mentor"),
    panel: t("portal.chooser.title", "Choose a panel"),
  }[choice];

  const open = (patientId: string) => {
    switchTo(patientId);
    navigate("/portal", { replace: true });
  };

  return (
    <AuthLayout
      panelTagline={t("portal.login.panelTagline")}
      panelTitle={t("portal.chooser.panelTitle", "Choose where you want to go today.")}
      colorScheme="portal"
    >
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-content-primary mb-2">{title}</h1>
          <p className="text-content-tertiary">
            {t(
              "portal.chooser.subtitle",
              "You can switch between them at any time from the menu.",
            )}
          </p>
        </div>

        <ul className="flex flex-col gap-2">
          {records.map((record) => (
            <li key={record.patient_id}>
              <button
                type="button"
                onClick={() => open(record.patient_id)}
                className="flex w-full items-center gap-3 rounded-2xl border border-gray-200 dark:border-gray-700 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-white/5 focus-visible:outline-2 focus-visible:outline-brand-500 transition-colors"
              >
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="truncate font-medium text-content-primary">
                      {recordLabel(record)}
                    </span>
                    <span className="shrink-0 rounded bg-gray-100 dark:bg-white/5 px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide text-content-tertiary">
                      {t(`portal.recordKind.${recordKind(record)}`)}
                    </span>
                  </span>
                  {recordSubtitle(record) && (
                    <span className="flex itemsateems-center gap-2 text-xs text-content-tertiary">
                      <span className="truncate">{recordSubtitle(record)}</span>
                      {recordSubtitleKind(record) && (
                        <span className="shrink-0 uppercase tracking-wide text-xs">
                          {t(`portal.recordKind.${recordSubtitleKind(record)}`)}
                        </span>
                      )}
                    </span>
                  )}
                </span>
                {record.inactive && (
                  <span className="shrink-0 rounded-full bg-gray-100 dark:bg-white/5 px-2 py-0.5 text-xs text-content-tertiary">
                    {t("portal.switcher.inactiveBadge", "Inactive")}
                  </span>
                )}
                <ChevronRight
                  className="w-4 h-4 shrink-0 text-content-tertiary"
                  aria-hidden="true"
                />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </AuthLayout>
  );
}
